// client/src/components/pages/LexiStar/Kpis/kpiTable.ts
//
// The KPI tables' logic (ST-00188 M4-D, T7-S02; m4-contracts §4 metric set):
// which columns a tab shows, merging one breakdown per column into rows, and
// sorting. A column appears on a tab only when the caller can read its metric
// and the metric is kept for that scope (MetricDefinition.scopes) — utilisation
// is a truck figure, so it shows on Trucks and not on Lanes.

import type { BreakdownBy } from '../../../../types/lexistar-analytics';
import type { PeriodState, SparkPoint } from '../Dashboard/analyticsAtoms';
import type { Better } from '../Dashboard/metricFormat';
import type { Breakdown, MetricDef } from '../Dashboard/kpiModel';

export type TabId = Extract<BreakdownBy, 'lane' | 'route' | 'asset' | 'driver' | 'run'>;

export const TABS: { id: TabId; label: string; noun: string }[] = [
  { id: 'lane', label: 'Lanes', noun: 'Lane' },
  { id: 'route', label: 'Routes', noun: 'Route' },
  { id: 'asset', label: 'Trucks', noun: 'Truck' },
  { id: 'driver', label: 'Drivers', noun: 'Driver' },
  { id: 'run', label: 'Runs', noun: 'Run' },
];

export const isTab = (v: string | null): v is TabId => TABS.some((t) => t.id === v);

export interface ColumnSpec {
  key: string;
  label: string;
  short: string;
  better: Better;
}

/**
 * T7-S02 columns: volume first (trips, tonnes), then the efficiency figures
 * the contract names — empty-running %, cycle time, utilisation, fuel
 * variance, on-route %, review share.
 */
export const COLUMN_SPECS: ColumnSpec[] = [
  { key: 'trip.completed', label: 'Trips completed', short: 'Trips', better: 'up' },
  { key: 'trip.tonnes_moved', label: 'Tonnes moved', short: 'Tonnes', better: 'up' },
  { key: 'run.empty_running_pct', label: 'Empty running', short: 'Empty %', better: 'down' },
  { key: 'lane.avg_cycle_min', label: 'Average cycle time', short: 'Cycle', better: 'down' },
  { key: 'asset.utilisation_pct', label: 'Utilisation', short: 'Util. %', better: 'up' },
  { key: 'trip.fuel_variance_l', label: 'Fuel against budget', short: 'Fuel var.', better: 'down' },
  { key: 'route.on_route_compliance_pct', label: 'On-route compliance', short: 'On-route', better: 'up' },
  { key: 'trip.review_share_pct', label: 'Share of trips in review', short: 'Review %', better: 'down' },
];

export interface Column extends ColumnSpec {
  def: MetricDef;
}

/** The columns a tab shows, in contract order. */
export function columnsFor(tab: TabId, catalogue: Map<string, MetricDef>, specs: ColumnSpec[] = COLUMN_SPECS): Column[] {
  return specs
    .map((spec) => {
      const def = catalogue.get(spec.key);
      return def ? { ...spec, def } : null;
    })
    .filter((c): c is Column => c !== null && (c.def.scopes.length === 0 || (c.def.scopes as string[]).includes(tab)));
}

export interface Cell {
  value: number | null;
  unit: string | null;
  provenance: string | null;
  drill: string | null;
  /** The row's trend for this column's metric (breakdown `series=true`); null when not sent. */
  series: SparkPoint[] | null;
}

export interface Row {
  id: string;
  label: string;
  cells: Record<string, Cell>;
  state: PeriodState | null;
  /** The first drill link any column gave for this row (the trips behind it). */
  drill: string | null;
}

/** One breakdown per column → one row per entity, in first-seen order. */
export function mergeBreakdowns(byKey: Record<string, Breakdown | undefined>, columns: Column[]): Row[] {
  const rows = new Map<string, Row>();
  columns.forEach((c) => {
    (byKey[c.key]?.rows || []).forEach((r) => {
      let row = rows.get(r.id);
      if (!row) {
        row = { id: r.id, label: r.label, cells: {}, state: null, drill: null };
        rows.set(r.id, row);
      }
      if (row.label === row.id && r.label) row.label = r.label;
      row.cells[c.key] = { value: r.value, unit: r.unit, provenance: r.provenance, drill: r.drill, series: r.series };
      if (r.state === 'provisional') row.state = 'provisional';
      else if (!row.state && r.state === 'final') row.state = 'final';
      if (!row.drill && r.drill) row.drill = r.drill;
    });
  });
  return [...rows.values()];
}

export interface Sort {
  key: string;
  dir: 'asc' | 'desc';
}

/** Sort by a metric column (nulls last, whatever the direction) or by name ('label'). */
export function sortRows(rows: Row[], sort: Sort): Row[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (sort.key === 'label') return sign * a.label.localeCompare(b.label, 'en-GB', { numeric: true });
    const va = a.cells[sort.key]?.value;
    const vb = b.cells[sort.key]?.value;
    const na = va === null || va === undefined;
    const nb = vb === null || vb === undefined;
    if (na && nb) return a.label.localeCompare(b.label);
    if (na) return 1;
    if (nb) return -1;
    return sign * ((va as number) - (vb as number)) || a.label.localeCompare(b.label);
  });
}

/** ?sort=<key>&dir=asc|desc, defaulting to the first column, highest first. */
export function readSort(params: URLSearchParams, columns: Column[]): Sort {
  const key = params.get('sort');
  const dir = params.get('dir') === 'asc' ? 'asc' : 'desc';
  if (key === 'label') return { key, dir };
  if (key && columns.some((c) => c.key === key)) return { key, dir };
  return { key: columns[0]?.key ?? 'label', dir: columns[0] ? 'desc' : 'asc' };
}

/**
 * The trend a row shows for `key`: from the breakdown's own series when the API
 * sent them (a row the metric has no figure for gets an empty line), else
 * undefined — the page then asks GET /kpis for that row (older servers).
 */
export function inlineTrend(row: Row, key: string, breakdown: Breakdown | undefined): SparkPoint[] | undefined {
  if (!breakdown?.hasSeries) return undefined;
  return row.cells[key]?.series ?? [];
}

/** Run `fn` over `items` with at most `limit` in flight (fallback sparkline fetches). */
export async function mapLimit<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i] as T);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}
